import React from "react";

interface OrderSummaryStepProps {
  eventName: string;
  eventPrice: number;
  paidAttendeeCount: number;
  waitlistedAttendeeCount: number;
  availableSeats: number;
  voucherSeatsUsed: number;
  codeDiscountAmount: number;
  adminDiscountAmount: number;
  codeDiscountPerSeat: number;
  adminDiscountPerSeat: number;
  paidSeatsAfterVoucher: number;
  total: number;
  totalCapacity: number;
  children?: React.ReactNode;
}

export default function OrderSummaryStep({
  eventName,
  eventPrice,
  paidAttendeeCount,
  waitlistedAttendeeCount,
  availableSeats,
  voucherSeatsUsed,
  codeDiscountAmount,
  adminDiscountAmount,
  codeDiscountPerSeat,
  adminDiscountPerSeat,
  paidSeatsAfterVoucher,
  total,
  children,
}: OrderSummaryStepProps) {
  const subtotal = eventPrice * paidAttendeeCount;
  const voucherDiscount =
    eventPrice * Math.min(voucherSeatsUsed, paidAttendeeCount);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 text-gray-900 dark:text-gray-200">
      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-4">
        Order Summary
      </h3>

      <div className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span>
            {paidAttendeeCount} Attendee
            {paidAttendeeCount !== 1 ? "s" : ""} (Paid)
          </span>
          <span className="text-muted-foreground truncate ml-2 text-right">
            {eventName}
          </span>
        </div>

        {waitlistedAttendeeCount > 0 && (
          <div className="text-sm text-muted-foreground">
            {waitlistedAttendeeCount} attendee
            {waitlistedAttendeeCount !== 1 ? "s" : ""} on waitlist (no charge)
          </div>
        )}

        <div className="border-t border-gray-200 dark:border-slate-700 my-3" />

        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span>Registration Fee</span>
            <span>
              ${eventPrice.toFixed(2)} &times; {paidAttendeeCount}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>

          {voucherSeatsUsed > 0 && (
            <div className="text-green-600 dark:text-green-400">
              <div className="flex justify-between font-medium">
                <span>Voucher applied</span>
                <span>-${voucherDiscount.toFixed(2)}</span>
              </div>
              <div className="text-right text-xs text-muted-foreground">
                {Math.min(voucherSeatsUsed, paidAttendeeCount)} seat
                {Math.min(voucherSeatsUsed, paidAttendeeCount) !== 1
                  ? "s"
                  : ""}
              </div>
            </div>
          )}

          {codeDiscountAmount > 0 && (
            <div className="text-blue-600 dark:text-blue-400">
              <div className="flex justify-between font-medium">
                <span>Discount code</span>
                <span>-${codeDiscountAmount.toFixed(2)}</span>
              </div>
              <div className="text-right text-xs text-muted-foreground">
                ${codeDiscountPerSeat.toFixed(2)} &times; {paidSeatsAfterVoucher}{" "}
                seat{paidSeatsAfterVoucher !== 1 ? "s" : ""}
              </div>
            </div>
          )}

          {adminDiscountAmount > 0 && (
            <div className="text-purple-600 dark:text-purple-400">
              <div className="flex justify-between font-medium">
                <span>Admin discount</span>
                <span>-${adminDiscountAmount.toFixed(2)}</span>
              </div>
              <div className="text-right text-xs text-muted-foreground">
                ${adminDiscountPerSeat.toFixed(2)} &times;{" "}
                {paidSeatsAfterVoucher} seat
                {paidSeatsAfterVoucher !== 1 ? "s" : ""}
              </div>
            </div>
          )}

          <div className="border-t border-gray-200 dark:border-slate-700 my-3" />

          <div className="flex justify-between text-lg font-bold dark:text-white">
            <span>Total</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>

        {/* CTA slot — rendered below the total */}
        {children && (
          <div className="pt-3">
            {children}
          </div>
        )}

        <div className="border-t border-gray-200 dark:border-slate-700 my-3" />

        <div className="text-sm flex justify-between">
          <span>Available seats</span>
          {availableSeats > 0 ? (
            <span>{availableSeats}</span>
          ) : (
            <span className="text-muted-foreground">Waitlist only</span>
          )}
        </div>
      </div>
    </div>
  );
}
